import { useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { paraphraseSegments, type Strength, type Segment } from "@/lib/paraphrase";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { looksNonEnglish } from "./paraphraseLanguage";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { MODE_INFO, noChangesMessage, resultIsStale, staleMessage, type ShownResult } from "./paraphraseModes";

const TOOL_INFO_CONTENT = {
  what: `Rewrites English text with safe, hand-checked phrase and word swaps — and, on the Strong setting, simple sentence changes such as moving a "because" clause to the front — so the grammar stays intact. It is a light rewriter, not an AI one: expect careful edits, not a full rewrite.`,
  how: `Paste your text, pick a strength (Light, Medium or Strong) and press Paraphrase. Changed words are highlighted — click one to put the original back. Press "Rewrite again" for a different version.`,
  tip: `Treat the output as a starting draft to edit, not a finished result — the best writing still benefits from a human pass to make sure it actually sounds like you and fits the context it's going into, rather than reading like a generic rewrite.`,
};

export default function TextParaphraser() {
  const { gateShare } = useShareGate();
  const [input, setInput] = useState("");
  const [segs, setSegs] = useState<Segment[]>([]);
  const [strength, setStrength] = useState<Strength>("medium");
  const [seed, setSeed] = useState(0);
  // The strength and text the result on screen was made from, and a message about the last click (B-0150, B-0154).
  const [shown, setShown] = useState<ShownResult | null>(null);
  const [note, setNote] = useState("");

  const { logUsageNow } = useLogUsageOnAction();

  const generate = (nextSeed: number) => {
    setSeed(nextSeed);
    const out = paraphraseSegments(input, strength, nextSeed);
    setSegs(out);
    setShown({ strength, input });
    return out.some(x => x.original !== undefined);
  };

  // A run is charged only when it actually changes something; nothing is charged for "no changes".
  const run = async () => {
    if (!input.trim()) return;
    setNote("");
    if (looksNonEnglish(input)) {
      setSegs([]);
      setShown(null);
      const m = "This looks like it is not English. The paraphraser is English only, so no changes were made and no trial use was counted.";
      setNote(m);
      toast.info(m);
      return;
    }
    const out = paraphraseSegments(input, strength, 0);
    if (!out.some(x => x.original !== undefined)) {
      setSegs(out);
      setShown({ strength, input });
      const m = noChangesMessage(strength);
      setNote(m);
      toast.info(m);
      return;
    }
    const allowed = await logUsageNow("generate");
    if (!allowed) {
      // Never leave an earlier result on screen as if it were this click's answer.
      setSegs([]);
      setShown(null);
      setNote("Nothing was paraphrased because today's trial uses are used up. Your text is still here.");
      return;
    }
    generate(0);
  };

  const stale = resultIsStale(shown, strength, input);
  const output = segs.map(x => x.text).join("");
  const changes = segs.filter(x => x.original !== undefined).length;
  const wc = (t: string) => (t.trim() ? t.trim().split(/\s+/).length : 0);
  const revert = (i: number) => setSegs(prev => prev.map((x, j) => (j === i ? { text: x.original!, } : x)));

  return (
    <ToolContainer>
      <div className="space-y-4">
        <Textarea aria-label="Text to paraphrase (English)" value={input} onChange={e => setInput(e.target.value)} placeholder="Enter English text to paraphrase..." className="min-h-[150px]" />
        <p className="text-xs text-muted-foreground">{wc(input)} words · {input.length} characters</p>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Strength">
          <span className="text-sm">Strength:</span>
          {(["light", "medium", "strong"] as Strength[]).map(st => (
            <Button key={st} size="sm" variant={strength === st ? "default" : "outline"} onClick={() => setStrength(st)} aria-pressed={strength === st} aria-label={`Strength: ${MODE_INFO[st].label}`} aria-describedby="para-mode-hint">
              {MODE_INFO[st].label}
            </Button>
          ))}
        </div>
        <p id="para-mode-hint" className="text-xs text-muted-foreground">{MODE_INFO[strength].hint}</p>
        <div className="flex gap-2">
          <Button onClick={run} disabled={!input.trim()} className="gap-2">
            <Sparkles className="h-4 w-4" />
            Paraphrase
          </Button>
          {segs.length > 0 && !stale && <Button variant="outline" onClick={() => { if (!generate(seed + 1)) toast.info("No safe changes found this time."); }} disabled={!input.trim()}>Rewrite again</Button>}
        </div>
        {note && !stale && <p role="status" data-testid="para-note" className="text-sm text-muted-foreground">{note}</p>}
        {segs.length > 0 && (
          <div>
            {stale && shown && <p role="status" data-testid="para-stale" className="mb-2 text-sm text-amber-700 dark:text-amber-400">{staleMessage(shown, strength, input)}</p>}
            <div className={`p-3 rounded-lg border bg-card text-sm whitespace-pre-wrap min-h-[150px]${stale ? " opacity-50" : ""}`} data-testid="para-output" aria-live="polite">
              {segs.map((x, i) => x.original !== undefined
                ? <mark key={i} title={`Original: ${x.original} — click to restore`} onClick={() => revert(i)} className="cursor-pointer rounded bg-primary/15 px-0.5 text-foreground">{x.text}</mark>
                : <span key={i}>{x.text}</span>)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{changes} change{changes === 1 ? "" : "s"} highlighted · {wc(output)} words · {output.length} characters</p>
            <Button size="sm" className="mt-2" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          English only. Makes safe, hand-checked word, phrase and sentence changes — it is not an AI rewriter, so review the result. Runs in your browser: your text is never sent anywhere, though a plain usage tick (tool name and an anonymous device id) is recorded.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
